import {
  Box,
  Button,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Typography,
  Chip,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import useMediaQuery from "@mui/material/useMediaQuery";
import axios from "axios";
import { Formik } from "formik";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import Header from "../../components/Header";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { TIME_OPTIONS } from "../../Utils/worshipTime";

const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

// ─── Open Hours Row ────────────────────────────────────────────────────────────
const OpenHourRow = ({ row, index, onChange, onRemove }) => (
  <Box
    display="flex"
    gap="12px"
    alignItems="center"
    sx={{
      backgroundColor: "rgba(104,112,250,0.07)",
      borderRadius: "8px",
      p: "12px",
      mb: "10px",
    }}
  >
    <Select size="small" variant="filled" value={row.day} onChange={(e) => onChange(index, "day", e.target.value)} sx={{ minWidth: 130 }}>
      {DAYS.map((d) => <MenuItem key={d} value={d.toLowerCase()}>{d}</MenuItem>)}
    </Select>
    <Select size="small" variant="filled" value={row.from} onChange={(e) => onChange(index, "from", e.target.value)} sx={{ minWidth: 110 }} displayEmpty>
      <MenuItem disabled value="">From</MenuItem>
      {TIME_OPTIONS.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
    </Select>
    <Typography sx={{ color: "#aaa" }}>–</Typography>
    <Select size="small" variant="filled" value={row.to} onChange={(e) => onChange(index, "to", e.target.value)} sx={{ minWidth: 110 }} displayEmpty>
      <MenuItem disabled value="">To</MenuItem>
      {TIME_OPTIONS.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
    </Select>
    <IconButton onClick={() => onRemove(index)} size="small" sx={{ color: "#f44336" }}>
      <DeleteOutlineIcon />
    </IconButton>
  </Box>
);

// ─── Main Component ────────────────────────────────────────────────────────────
const AddWorshipCenters = () => {
  const isNonMobile = useMediaQuery("(min-width:600px)");
  const navigate = useNavigate();

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [alertSeverity, setAlertSeverity] = useState("success");
  const [alertMessage, setAlertMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Open Hours state
  const [openHours, setOpenHours] = useState([{ day: "sunday", from: "", to: "" }]);
  const [openHoursError, setOpenHoursError] = useState("");

  // Services state: each slot = { day, from, to, names: string[] }
  // On submit: names[] → joined as "service1,service2" → 4th element
  const [services, setServices] = useState([{ day: "sunday", from: "", to: "", names: [] }]);
  const [servicesError, setServicesError] = useState("");

  // Draft input per slot index: { [slotIndex]: string }
  const [draftNames, setDraftNames] = useState({ 0: "" });

  // ── Open Hours handlers
  const handleOpenHourChange = (index, field, value) => {
    const updated = [...openHours];
    updated[index][field] = value;
    setOpenHours(updated);
  };
  const addOpenHour = () => setOpenHours([...openHours, { day: "sunday", from: "", to: "" }]);
  const removeOpenHour = (index) => setOpenHours(openHours.filter((_, i) => i !== index));

  // ── Service slot handlers
  const handleSlotChange = (index, field, value) => {
    const updated = [...services];
    updated[index][field] = value;
    setServices(updated);
  };

  const addSlot = () => {
    const newIndex = services.length;
    setServices((prev) => [...prev, { day: "sunday", from: "", to: "", names: [] }]);
    setDraftNames((prev) => ({ ...prev, [newIndex]: "" }));
  };

  const removeSlot = (index) => {
    setServices((prev) => prev.filter((_, i) => i !== index));
    // Re-index draftNames
    setDraftNames((prev) => {
      const updated = {};
      Object.keys(prev).forEach((k) => {
        const ki = parseInt(k);
        if (ki < index) updated[ki] = prev[k];
        else if (ki > index) updated[ki - 1] = prev[k];
      });
      return updated;
    });
  };

  // Add one service name into a slot's names array
  const commitName = (slotIndex) => {
    const name = (draftNames[slotIndex] || "").trim();
    if (!name) return;
    setServices((prev) => {
      const updated = [...prev];
      updated[slotIndex] = {
        ...updated[slotIndex],
        names: [...updated[slotIndex].names, name],
      };
      return updated;
    });
    setDraftNames((prev) => ({ ...prev, [slotIndex]: "" }));
    setServicesError("");
  };

  const removeName = (slotIndex, nameIndex) => {
    setServices((prev) => {
      const updated = [...prev];
      updated[slotIndex] = {
        ...updated[slotIndex],
        names: updated[slotIndex].names.filter((_, i) => i !== nameIndex),
      };
      return updated;
    });
  };

  const getServicesWithDrafts = () =>
    services.map((slot, slotIndex) => {
      const pendingName = (draftNames[slotIndex] || "").trim();
      if (!pendingName) return slot;
      return { ...slot, names: [...slot.names, pendingName] };
    });

  // ── Validate
  const validateDynamic = () => {
    const servicesToValidate = getServicesWithDrafts();
    let valid = true;
    const ohErr = openHours.some((r) => !r.from || !r.to);
    if (ohErr) { setOpenHoursError("Please complete all open hour entries."); valid = false; }
    else setOpenHoursError("");

    const svcErr = servicesToValidate.some((r) => !r.from || !r.to || r.names.length === 0);
    if (svcErr) {
      setServicesError("Each slot needs from/to times and at least one service name.");
      valid = false;
    } else setServicesError("");

    return valid;
  };

  const handleFormSubmit = async (values) => {
    if (!validateDynamic()) return;

    const servicesToSave = getServicesWithDrafts();
    setServices(servicesToSave);
    setDraftNames({});

    // Result: [["sunday","8.00","18.00","service1,service2"], ...]
    const servicePayload = JSON.stringify(
      servicesToSave.map((r) => [r.day, r.from, r.to, r.names.join(",")])
    );

    const openHoursPayload = JSON.stringify(
      openHours.map((r) => [r.day, r.from, r.to])
    );

    const formData = new FormData();
    formData.append("church", values.church);
    formData.append("address", values.address);
    formData.append("email", values.email);
    formData.append("tel", values.tel);
    formData.append("noOfUsers", "0");
    formData.append("description", values.description);
    formData.append("longtitude", values.longitude);
    formData.append("latitude", values.latitude);
    formData.append("image", values.image);
    formData.append("paster", values.paster);
    formData.append("status", values.status);
    formData.append("openHours", openHoursPayload);
    formData.append("service", servicePayload);

    setIsLoading(true);
    try {
      const response = await axios.post(
        "https://backend.jcgn.org/api/worshipCenter/createWorshipCenter",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      if (response.data.status) {
        setAlertSeverity("success");
        setAlertMessage("Worship Center added successfully");
        setTimeout(() => navigate("/worship-centers"), 2500);
      }
    } catch (error) {
      setAlertSeverity("error");
      setAlertMessage(`Failed to add Worship Center: ${error.response?.data?.message || error.message}`);
    } finally {
      setOpenSnackbar(true);
      setIsLoading(false);
    }
  };

  return (
    <Box m="20px" height="70vh" overflow="auto" paddingRight="20px">
      <Header title="Add Worship Center" subtitle="Create a New Worship Center" />

      <Formik onSubmit={handleFormSubmit} initialValues={initialValues} validationSchema={checkoutSchema}>
        {({ values, errors, touched, handleBlur, handleChange, handleSubmit }) => (
          <form onSubmit={handleSubmit}>
            <Box display="grid" gap="30px" gridTemplateColumns={isNonMobile ? "repeat(2, 1fr)" : "repeat(1, 1fr)"}>
              <Box>
                <TextField fullWidth variant="filled" label="Church Name" onBlur={handleBlur} onChange={handleChange} value={values.church} name="church" error={!!touched.church && !!errors.church} helperText={touched.church && errors.church} />
              </Box>
              <Box>
                <TextField fullWidth variant="filled" label="Address" onBlur={handleBlur} onChange={handleChange} value={values.address} name="address" error={!!touched.address && !!errors.address} helperText={touched.address && errors.address} />
              </Box>
              <Box>
                <TextField fullWidth variant="filled" label="Email" onBlur={handleBlur} onChange={handleChange} value={values.email} name="email" error={!!touched.email && !!errors.email} helperText={touched.email && errors.email} />
              </Box>
              <Box>
                <TextField fullWidth variant="filled" label="Telephone" onBlur={handleBlur} onChange={handleChange} value={values.tel} name="tel" error={!!touched.tel && !!errors.tel} helperText={touched.tel && errors.tel} />
              </Box>
              <Box>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  variant="filled"
                  label={`Description (${countWords(values.description)}/${MAX_DESCRIPTION_WORDS} words)`}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.description}
                  name="description"
                  error={!!touched.description && !!errors.description}
                  helperText={
                    (touched.description && errors.description) ||
                    `Write a short description in ${MAX_DESCRIPTION_WORDS} words or less.`
                  }
                  placeholder="Brief description of this worship center"
                />
              </Box>
              <Box>
                <TextField fullWidth variant="filled" label="Paster Name" onBlur={handleBlur} onChange={handleChange} value={values.paster} name="paster" error={!!touched.paster && !!errors.paster} helperText={touched.paster && errors.paster} />
              </Box>
              <Box>
                <TextField fullWidth variant="filled" label="Latitude" onBlur={handleBlur} onChange={handleChange} value={values.latitude} name="latitude" error={!!touched.latitude && !!errors.latitude} helperText={touched.latitude && errors.latitude} />
              </Box>
              <Box>
                <TextField fullWidth variant="filled" label="Longitude" onBlur={handleBlur} onChange={handleChange} value={values.longitude} name="longitude" error={!!touched.longitude && !!errors.longitude} helperText={touched.longitude && errors.longitude} />
              </Box>
              <Box>
                <InputLabel htmlFor="status">Status</InputLabel>
                <Select fullWidth variant="filled" value={values.status} onChange={handleChange} onBlur={handleBlur} name="status" error={!!touched.status && !!errors.status}>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
                {touched.status && errors.status && (
                  <FormHelperText style={{ color: "#f44336", paddingLeft: "15px" }}>{errors.status}</FormHelperText>
                )}
              </Box>
              <Box>
                <InputLabel htmlFor="image">Upload an Image *</InputLabel>
                <TextField fullWidth variant="filled" type="file" inputProps={{ accept: "image/*" }} onChange={(event) => handleChange({ target: { name: "image", value: event.currentTarget.files[0] } })} name="image" error={!!touched.image && !!errors.image} helperText={touched.image && errors.image} />
              </Box>
            </Box>

            {/* ── Open Hours Section ── */}
            <Box mt="30px">
              <Box display="flex" alignItems="center" justifyContent="space-between" mb="12px">
                <Typography variant="h6" fontWeight="600">Open Hours</Typography>
                <Button startIcon={<AddCircleOutlineIcon />} onClick={addOpenHour} sx={{ color: "#6870fa", textTransform: "none" }}>Add Row</Button>
              </Box>
              {openHours.map((row, index) => (
                <OpenHourRow key={index} row={row} index={index} onChange={handleOpenHourChange} onRemove={removeOpenHour} />
              ))}
              {openHoursError && <FormHelperText style={{ color: "#f44336", paddingLeft: "4px" }}>{openHoursError}</FormHelperText>}
              <Typography variant="caption" sx={{ color: "#888", mt: "4px", display: "block" }}>
                Preview: {JSON.stringify(openHours.map((r) => [r.day, r.from, r.to]))}
              </Typography>
            </Box>

            {/* ── Services Section ── */}
            <Box mt="30px">
              <Box display="flex" alignItems="center" justifyContent="space-between" mb="12px">
                <Typography variant="h6" fontWeight="600">Services</Typography>
                <Button startIcon={<AddCircleOutlineIcon />} onClick={addSlot} sx={{ color: "#6870fa", textTransform: "none" }}>
                  Add Slot
                </Button>
              </Box>

              {services.map((slot, slotIndex) => (
                <Box
                  key={slotIndex}
                  sx={{
                    backgroundColor: "rgba(104,112,250,0.07)",
                    borderRadius: "8px",
                    p: "14px",
                    mb: "14px",
                  }}
                >
                  {/* Day + Time selectors + remove slot button */}
                  <Box display="flex" gap="12px" alignItems="center" flexWrap="wrap" mb="12px">
                    <Select size="small" variant="filled" value={slot.day} onChange={(e) => handleSlotChange(slotIndex, "day", e.target.value)} sx={{ minWidth: 130 }}>
                      {DAYS.map((d) => <MenuItem key={d} value={d.toLowerCase()}>{d}</MenuItem>)}
                    </Select>

                    <Select size="small" variant="filled" value={slot.from} onChange={(e) => handleSlotChange(slotIndex, "from", e.target.value)} sx={{ minWidth: 110 }} displayEmpty>
                      <MenuItem disabled value="">From</MenuItem>
                      {TIME_OPTIONS.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                    </Select>

                    <Typography sx={{ color: "#aaa" }}>–</Typography>

                    <Select size="small" variant="filled" value={slot.to} onChange={(e) => handleSlotChange(slotIndex, "to", e.target.value)} sx={{ minWidth: 110 }} displayEmpty>
                      <MenuItem disabled value="">To</MenuItem>
                      {TIME_OPTIONS.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                    </Select>

                    <IconButton onClick={() => removeSlot(slotIndex)} size="small" sx={{ color: "#f44336", ml: "auto" }}>
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Box>

                  {/* Service name chips for this slot */}
                  {slot.names.length > 0 && (
                    <Box display="flex" flexWrap="wrap" gap="6px" mb="10px">
                      {slot.names.map((name, nameIndex) => (
                        <Chip
                          key={nameIndex}
                          label={name}
                          onDelete={() => removeName(slotIndex, nameIndex)}
                          size="small"
                          sx={{
                            backgroundColor: "rgba(104,112,250,0.2)",
                            color: "inherit",
                            fontWeight: 500,
                            "& .MuiChip-deleteIcon": { color: "#f44336" },
                          }}
                        />
                      ))}
                    </Box>
                  )}

                  {/* Input to add one service name at a time */}
                  <Box display="flex" gap="10px" alignItems="center">
                    <TextField
                      size="small"
                      variant="filled"
                      placeholder="Type a service name (press Enter or Add, or it saves on submit)"
                      value={draftNames[slotIndex] || ""}
                      onChange={(e) => setDraftNames((prev) => ({ ...prev, [slotIndex]: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commitName(slotIndex); } }}
                      sx={{ flexGrow: 1 }}
                    />
                    <Button
                      variant="outlined"
                      onClick={() => commitName(slotIndex)}
                      sx={{
                        color: "#6870fa",
                        borderColor: "#6870fa",
                        textTransform: "none",
                        whiteSpace: "nowrap",
                        "&:hover": { borderColor: "#3e4396", color: "#3e4396" },
                      }}
                    >
                      + Add
                    </Button>
                  </Box>

                  {/* Per-slot stored value preview */}
                  <Typography variant="caption" sx={{ color: "#888", mt: "6px", display: "block" }}>
                    Stored as: {JSON.stringify([slot.day, slot.from, slot.to, slot.names.join(",")])}
                  </Typography>
                </Box>
              ))}

              {servicesError && (
                <FormHelperText style={{ color: "#f44336", paddingLeft: "4px" }}>{servicesError}</FormHelperText>
              )}

              <Typography variant="caption" sx={{ color: "#888", mt: "4px", display: "block" }}>
                Full payload: {JSON.stringify(services.map((r) => [r.day, r.from, r.to, r.names.join(",")]))}
              </Typography>
            </Box>

            {/* ── Submit ── */}
            <Box display="flex" justifyContent="end" mt="30px">
              <Button
                type="submit"
                variant="contained"
                sx={{
                  backgroundColor: "#6870fa",
                  color: "white",
                  fontSize: "16px",
                  px: "32px",
                  "&:hover": { backgroundColor: "#3e4396" },
                }}
              >
                {isLoading ? "Adding..." : "Add Worship Center"}
              </Button>
            </Box>
          </form>
        )}
      </Formik>

      <Snackbar open={openSnackbar} autoHideDuration={5000} onClose={() => setOpenSnackbar(false)} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
        <MuiAlert onClose={() => setOpenSnackbar(false)} severity={alertSeverity} elevation={6} variant="filled" style={{ color: "white" }}>
          {alertSeverity === "success" ? "Success" : "Error"}: {alertMessage}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
};

// ─── Validation ────────────────────────────────────────────────────────────────
const MAX_DESCRIPTION_WORDS = 50;
const countWords = (text = "") =>
  text.trim().split(/\s+/).filter(Boolean).length;

const phoneRegExp = /^((\+[1-9]{1,4}[ -]?)|(\([0-9]{2,3}\)[ -]?)|([0-9]{2,4})[ -]?)*?[0-9]{3,4}[ -]?[0-9]{3,4}$/;

const checkoutSchema = yup.object().shape({
  church: yup.string().required("Required"),
  address: yup.string().required("Required"),
  email: yup.string().email("Invalid email").required("Required"),
  tel: yup.string().matches(phoneRegExp, "Phone number is not valid").max(15, "Phone number must be at most 15 digits").required("Required"),
  description: yup
    .string()
    .required("Required")
    .test(
      "max-words",
      `Description must be ${MAX_DESCRIPTION_WORDS} words or less`,
      (value) => countWords(value) <= MAX_DESCRIPTION_WORDS,
    ),
  paster: yup.string().required("Required"),
  longitude: yup.number().typeError("Should be a number").required("Required"),
  latitude: yup.number().typeError("Should be a number").required("Required"),
  status: yup.string().required("Required"),
  image: yup.mixed().required("Image is required"),
});

// ─── Initial Values ────────────────────────────────────────────────────────────
const initialValues = {
  church: "",
  address: "",
  email: "",
  tel: "",
  description: "",
  paster: "",
  longitude: "",
  latitude: "",
  status: "active",
  image: null,
};

export default AddWorshipCenters;